import React from 'react';
import { MapContainer, TileLayer, Marker, Circle, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// (opcjonalnie) proste UI — możesz usunąć jeśli nie używasz MUI
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import RefreshIcon from '@mui/icons-material/Refresh';
import MyLocationIcon from '@mui/icons-material/MyLocation';

// --------------------- Typy ---------------------

type LocationData = {
  latitude: number;
  longitude: number;
  accuracy: number;
  timestamp: number;
  heading?: number | null; // stopnie [0..360), 0 = północ
  source: 'gps' | 'network' | 'unknown';
};

// --------------------- Utils ---------------------

function toRad(d: number) { return (d * Math.PI) / 180; }
function toDeg(r: number) { return (r * 180) / Math.PI; }

/** Bearing geodezyjny (stopnie 0..360), z (lat1,lon1) do (lat2,lon2). */
function computeBearing(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const φ1 = toRad(lat1), φ2 = toRad(lat2);
  const Δλ = toRad(lon2 - lon1);
  const y = Math.sin(Δλ) * Math.cos(φ2);
  const x = Math.cos(φ1) * Math.sin(φ2) - Math.sin(φ1) * Math.cos(φ2) * Math.cos(Δλ);
  const θ = Math.atan2(y, x);
  const deg = (toDeg(θ) + 360) % 360;
  return deg;
}

/** Prosty detektor ruchu — czy dystans > minMoveM */
function movedEnough(a: LocationData, b: LocationData, minMoveM = 1.5): boolean {
  const R = 6371000;
  const φ1 = toRad(a.latitude);
  const φ2 = toRad(b.latitude);
  const dφ = toRad(b.latitude - a.latitude);
  const dλ = toRad(b.longitude - a.longitude);
  const hav = Math.sin(dφ / 2) ** 2 + Math.cos(φ1) * Math.cos(φ2) * Math.sin(dλ / 2) ** 2;
  const d = 2 * R * Math.asin(Math.sqrt(hav));
  return d >= minMoveM;
}

/** Zrobienie „pucka” (kółko + strzałka) jako DivIcon. */
function makePuckIcon(bearingDeg: number | null | undefined) {
  const size = 30;                      // wielkość całego znacznika
  const arrowLength = 12;               // długość strzałki
  const arrowWidth = 8;                 // szerokość podstawy strzałki
  const ringColor = 'rgba(0, 128, 255, 0.95)'; // obwód kółka
  const fillColor = 'rgba(0, 128, 255, 0.20)'; // wypełnienie
  const dotColor  = 'rgba(0, 128, 255, 1.00)'; // kropka w środku
  const arrowColor= 'rgba(0, 0, 0, 0.6)';      // kolor strzałki

  // Strzałka jako SVG trójkąt; obracamy całą warstwę SVG wokół środka
  const svg = `
    <svg width="${size}" height="${size}" viewBox="0 0 ${size} ${size}"
         style="position:absolute; left:0; top:0; transform: rotate(${bearingDeg ?? 0}deg); transform-origin: 50% 50%;">
      <polygon
        points="${size/2},${(size/2 - arrowLength)} ${size/2 - arrowWidth/2},${size/2} ${size/2 + arrowWidth/2},${size/2}"
        fill="${arrowColor}"/>
    </svg>
  `;

  const html = `
    <div style="position:relative; width:${size}px; height:${size}px;">
      ${bearingDeg == null ? '' : svg}
      <div style="
        position:absolute; left:0; top:0; width:${size}px; height:${size}px;
        border-radius:50%;
        background:${fillColor};
        border:2px solid ${ringColor};
        box-shadow: 0 0 8px rgba(0,128,255,0.35) inset;
      "></div>
      <div style="
        position:absolute; width:6px; height:6px;
        left:${size/2 - 3}px; top:${size/2 - 3}px;
        border-radius:50%;
        background:${dotColor};
      "></div>
    </div>
  `;
  return L.divIcon({
    html,
    className: '',             // brak globalnej klasy — używamy inline styles
    iconSize: [size, size],
    iconAnchor: [size/2, size/2]
  });
}

// Komponent do podążania mapy za pozycją, gdy włączone follow
function FollowMap({ location, follow }: { location: LocationData | null; follow: boolean }) {
  const map = useMap();
  React.useEffect(() => {
    if (!location) return;
    if (follow) {
      // delikatne podążanie (zachowaj obecny zoom, min 17)
      map.setView([location.latitude, location.longitude], Math.max(map.getZoom(), 17), { animate: true });
    }
  }, [location, follow, map]);
  return null;
}

// --------------------- Główny komponent ---------------------

export default function LocationPage() {
  const [location, setLocation] = React.useState<LocationData | null>(null);
  const [prev, setPrev] = React.useState<LocationData | null>(null);
  const [watching, setWatching] = React.useState(true);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const watchIdRef = React.useRef<number | null>(null);

  // Bazowe centrum (Warszawa) zanim dostaniemy GPS
  const center = React.useMemo<[number, number]>(() => {
    return location ? [location.latitude, location.longitude] : [52.2297, 21.0122];
  }, [location]);

  const isSecure = React.useMemo(() => {
    return window.location.protocol === 'https:' ||
           window.location.hostname === 'localhost' ||
           window.location.hostname === '127.0.0.1';
  }, []);

  const updateFromPosition = (pos: GeolocationPosition) => {
    const acc = pos.coords.accuracy ?? 9999;
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    const src: LocationData['source'] =
      isMobile && acc < 50 ? 'gps' : acc > 100 ? 'network' : 'gps';

    const next: LocationData = {
      latitude: pos.coords.latitude,
      longitude: pos.coords.longitude,
      accuracy: acc,
      timestamp: pos.timestamp,
      heading: Number.isFinite(pos.coords.heading as number) ? pos.coords.heading : null,
      source: src
    };

    setPrev((p) => (p ? p : next)); // jeśli to pierwszy fix, prev=next
    setLocation(next);
  };

  const startWatch = React.useCallback(() => {
    if (!navigator.geolocation) {
      setError('Geolokacja nie jest wspierana przez tę przeglądarkę.');
      return;
    }
    if (!isSecure) {
      setError(`Geolokacja wymaga HTTPS lub localhost (teraz: ${window.location.protocol}).`);
      return;
    }
    setError(null);
    setLoading(true);
    if (watchIdRef.current !== null) {
      navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
    }
    const id = navigator.geolocation.watchPosition(
      (pos) => {
        updateFromPosition(pos);
        setLoading(false);
      },
      (err) => {
        setError(
          err.code === err.PERMISSION_DENIED
            ? 'Odmowa dostępu do lokalizacji.'
            : err.code === err.POSITION_UNAVAILABLE
            ? 'Lokalizacja niedostępna.'
            : err.code === err.TIMEOUT
            ? 'Przekroczono czas oczekiwania na lokalizację.'
            : 'Nieznany błąd geolokacji.'
        );
        setLoading(false);
      },
      {
        enableHighAccuracy: true,
        timeout: 20000,
        maximumAge: 0
      }
    );
    watchIdRef.current = id;
  }, [isSecure]);

  const stopWatch = React.useCallback(() => {
    if (watchIdRef.current !== null) {
      navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
    }
  }, []);

  React.useEffect(() => {
    if (watching) startWatch();
    return () => stopWatch();
  }, [watching, startWatch, stopWatch]);

  const manualRefresh = () => {
    if (!navigator.geolocation) return;
    setLoading(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => { updateFromPosition(pos); setLoading(false); },
      () => { setLoading(false); }
    );
  };

  // bearing do strzałki: preferuj GPS heading, w przeciwnym razie bearing z dwóch punktów
  const bearing = React.useMemo<number | null>(() => {
    if (!location) return null;
    if (location.heading != null && Number.isFinite(location.heading)) {
      // W specyfikacji 0° = North, rośnie zgodnie z ruchem wskazówek
      return location.heading!;
    }
    if (prev && movedEnough(prev, location, Math.max(2, location.accuracy * 0.35))) {
      return computeBearing(prev.latitude, prev.longitude, location.latitude, location.longitude);
    }
    return null;
  }, [location, prev]);

  // marker puck jako DivIcon
  const puckIcon = React.useMemo(() => makePuckIcon(bearing), [bearing]);

  return (
    <Box sx={{ p: { xs: 2, sm: 4 }, width: '100%', minHeight: '100vh' }}>
      <Card sx={{ borderRadius: 2, mb: 2 }}>
        <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <MyLocationIcon color="primary" />
              Live Location (Leaflet puck + arrow)
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              {loading && (<><CircularProgress size={16} /><Typography variant="caption">Aktualizuję…</Typography></>)}
              <FormControlLabel
                control={<Switch checked={watching} onChange={(e) => setWatching(e.target.checked)} />}
                label="Auto-śledzenie"
              />
              <Button size="small" variant="outlined" startIcon={<RefreshIcon />} onClick={manualRefresh}>
                Odśwież
              </Button>
            </Box>
          </Box>

          <Box sx={{ height: { xs: '60vh', sm: '500px' }, width: '100%' }}>
            <MapContainer
              center={center}
              zoom={17}
              style={{ height: '100%', width: '100%' }}
            >
              <TileLayer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                attribution='&copy; OpenStreetMap contributors'
              />
              {location && (
                <>
                  <Marker
                    position={[location.latitude, location.longitude]}
                    icon={puckIcon}
                  />
                  <Circle
                    center={[location.latitude, location.longitude]}
                    radius={Math.min(Math.max(location.accuracy || 0, 5), 250)} // 5–250 m
                    pathOptions={{ fillOpacity: 0.15, weight: 1 }}
                  />
                </>
              )}
              <FollowMap location={location} follow={watching} />
            </MapContainer>
          </Box>

          {location && (
            <Box sx={{ mt: 1.5 }}>
              <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                Lat: {location.latitude.toFixed(6)} | Lng: {location.longitude.toFixed(6)} | acc: ±{Math.round(location.accuracy)}m | source: {location.source} | bearing: {bearing == null ? '—' : `${bearing.toFixed(0)}°`}
              </Typography>
            </Box>
          )}

          {error && (
            <Typography color="error" sx={{ mt: 1.5 }}>{error}</Typography>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
